import { DateTime, formatToSQL, getDateTime } from 'infras/datetime';
import {
  DragEvent,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  EventListItem,
  MergeableEventListItem,
  Schedule,
} from 'presentations/views/components/atoms/Calendar/reducer';
import {
  CalendarContext,
  setCreatingEvent,
  setSchedules,
  setSelectedGroups,
  setSelectedSlots,
} from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import {
  defaultCacheExpiration,
  getPersistentItem,
  setPersistentItem,
} from 'presentations/views/helpers/storage';

interface Props {
  dates: DateTime[];
  startedAt: DateTime;
  onUpdateSchedule?: (schedule: Schedule) => void;
}

export function useAllDayEvent(props: Props) {
  const { state, dispatch } = useContext(CalendarContext);
  const [isDropped, setIsDropped] = useState<boolean>(false);
  const [draggingX, setDraggingX] = useState<number>(0);
  const [events, setEvents] = useState<MergeableEventListItem[]>([]);
  const [eventMaxRow, setEventMaxRow] = useState<number>(0);

  const [
    draggingGhostEvent,
    setDraggingGhostEvent,
  ] = useState<EventListItem | null>(null);
  const [draggingEvent, setDraggingEvent] = useState<EventListItem | null>(
    null,
  );
  const [dragDirection, setDragDirection] = useState<'left' | 'right' | null>(
    null,
  );
  const isActive = [
    state.currentPage - 1,
    state.currentPage,
    state.currentPage + 1,
  ].includes(state.pages.indexOf(props.startedAt));
  const isCurrentPage =
    state.pages.indexOf(props.startedAt) === state.currentPage;

  const cache = useMemo(() => {
    const gs = props.startedAt.toMillis();
    const schedules: Schedule[] =
      getPersistentItem(
        `${state.cachePrefix}:schedules-all-day-${state.showRange.day}-${gs}`,
      ) || [];
    return { schedules: schedules || null };
  }, [props.startedAt, state.showRange, state.cachePrefix]);

  const isUseCache = state.schedules === null || !isActive;
  const currentSchedules = useMemo(() => {
    if (!state.schedules) {
      return [];
    }
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    const currents = [];
    for (let i = 0; i < state.schedules.length; i++) {
      const current = state.schedules[i];
      if (!current.isAllDay) continue;
      const originalStartedAt = getDateTime(current.startedAt, state.timeZone);
      const originalEndedAt = getDateTime(current.endedAt, state.timeZone);
      const startedAt = getDateTime(
        {
          year: originalStartedAt.year,
          month: originalStartedAt.month,
          day: originalStartedAt.day,
        },
        state.timeZone,
      );
      const endedAt = getDateTime(
        {
          year: originalEndedAt.year,
          month: originalEndedAt.month,
          day: originalEndedAt.day,
        },
        state.timeZone,
      );

      const isOverlap = gridStartedAt < endedAt && gridEndedAt > startedAt;
      if (!isOverlap) continue;
      currents.push(current);
    }
    return currents;
  }, [state.schedules, cache, props.startedAt, state.showRange]);

  const schedules = isUseCache ? cache.schedules : currentSchedules;

  useEffect(() => {
    const eventListItems = [];
    const searchSchedules = [...schedules];
    const gridStartedAt = props.startedAt;
    const gridEndedAt = gridStartedAt.plus(state.showRange);
    if (state.creatingEvent) {
      const isOverlap =
        gridStartedAt < getDateTime(state.creatingEvent.endedAt) &&
        gridEndedAt > getDateTime(state.creatingEvent.startedAt);
      if (isOverlap) {
        searchSchedules.push(state.creatingEvent);
      }
    }
    const oneMinute = 1000 * 60;
    const oneDay = oneMinute * 60 * 24;

    const gs = gridStartedAt.toMillis();

    // 時間変換まわりの処理をまとめて行う
    // 中間処理した方がパフォーマンス的に速いかと思うのと、
    // 比較処理がシンプルになるのでやっているが不要ならまとめる
    for (let i = 0; i < searchSchedules.length; i++) {
      const current = searchSchedules[i];
      if (!current.isAllDay) continue;
      const originalStartedAt = getDateTime(current.startedAt, state.timeZone);
      const originalEndedAt = getDateTime(current.endedAt, state.timeZone);
      let startedAt = getDateTime(
        {
          year: originalStartedAt.year,
          month: originalStartedAt.month,
          day: originalStartedAt.day,
        },
        state.timeZone,
      );
      let endedAt = getDateTime(
        {
          year: originalEndedAt.year,
          month: originalEndedAt.month,
          day: originalEndedAt.day,
        },
        state.timeZone,
      );

      if (startedAt < gridStartedAt) {
        startedAt = gridStartedAt;
      }

      if (gridEndedAt < endedAt) {
        endedAt = gridEndedAt;
      }

      const cs = startedAt.toMillis();
      const ce = endedAt.toMillis();
      let x = (cs - gs) / oneDay;
      let xSpan = Math.round((ce - cs) / oneDay);
      // 前ページからのまたぎ
      if (x < 0) {
        xSpan = xSpan + x;
        x = 0;
      }
      const event: EventListItem = {
        scheduleType: current.scheduleType,
        uid: current.calendarId
          ? `${current.calendarId}-${current.uid}`
          : current.uid,
        layoutId: `${current.userId}-${current.uid}-${i}`,
        eventId: current.uid,
        calendarId: current.calendarId,
        isDraggable: true,
        name: current.name || '',
        isAllDay: current.isAllDay,
        x: x < 0 ? 0 : x,
        xSpan,
        y: 0,
        ySpan: 1,
        color: current.color,
        userId: current.userId,
        startedAt,
        endedAt,
        url: current.url,
        calendarSource: current.calendarSource,
        status: current.status,
        providerType: current.providerType,
        description: current.description,
        address: current.address,
        responseStatus: current.responseStatus,
        isRecurringEvent: current.isRecurringEvent,
        behavior: current.behavior,
      };
      eventListItems.push(event);
    }
    const fixedListItems = eventListItems.sort((a, b) => {
      if (a.xSpan && b.xSpan) {
        if (a.xSpan > b.xSpan) return -1;
      }
      return 0;
    });

    // イベントの重なりを作る
    let max = 0;
    for (let i = 0; i < fixedListItems.length; i++) {
      const event = fixedListItems[i];
      let position = 0;
      for (let j = 0; j < fixedListItems.length; j++) {
        const targetEvent = fixedListItems[j];
        if (event.layoutId === targetEvent.layoutId) continue;

        const cs = event.x;
        const ts = targetEvent.x;
        const te = targetEvent.x + targetEvent.xSpan!;
        const ce = event.x + event.xSpan!;
        const isOverlapX = cs < te && ce > ts;
        if (
          isOverlapX ||
          (event.x === targetEvent.x && event.xSpan === targetEvent.xSpan)
        ) {
          if (i > j) {
            position++;
            if (max < position) max = position;
          }
        }
      }
      fixedListItems[i].y = position;
    }
    setEvents(fixedListItems);
    setEventMaxRow(max);
  }, [schedules, state.showRange, state.creatingEvent, props.startedAt]);

  useEffect(() => {
    if (isUseCache) return;
    const gs = props.startedAt.toMillis();
    setPersistentItem(
      `${state.cachePrefix}:schedules-all-day-${state.showRange.day}-${gs}`,
      schedules,
      defaultCacheExpiration,
    );
  }, [
    isUseCache,
    schedules,
    eventMaxRow,
    props.startedAt,
    state.showRange,
    state.cachePrefix,
  ]);

  useEffect(() => {
    if (!isDropped) return;
    setDraggingEvent(null);
    setDraggingGhostEvent(null);
    setDragDirection(null);
    const elements = document.getElementsByClassName('js-allday-events');
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index] as HTMLElement;
      const { eventId } = element.dataset;
      if (eventId === draggingEvent?.eventId) {
        element.style.height = '';
      } else {
        element.style.pointerEvents = 'auto';
        element.style.userSelect = 'auto';
      }
    }
    setIsDropped(false);
  }, [isDropped]);

  const onEventDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.dataTransfer.effectAllowed = 'move';
      const { eventId } = e.currentTarget.dataset;
      if (!eventId) return;
      const event = events.find((e) => e.eventId === eventId);
      if (!event) return;
      setDraggingEvent(event);
      setDraggingGhostEvent(event);
      disableEvents(event.eventId);
      setDraggingX(event.x);
    },
    [events],
  );

  const disableEvents = (dragEventId: string) => {
    const elements = document.getElementsByClassName('js-allday-events');
    for (let index = 0; index < elements.length; index++) {
      const element = elements[index] as HTMLElement;
      const { eventId } = element.dataset;
      if (eventId === dragEventId) {
        setTimeout(() => {
          element.style.height = '1px';
        }, 100);
      } else {
        element.style.pointerEvents = 'none';
        element.style.userSelect = 'none';
      }
    }
  };

  const onEventDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      console.debug('enter');
      if (!draggingEvent) return;
      const { frameId } = e.currentTarget.dataset;
      const event = events.find((e) => e.eventId === draggingEvent.eventId);
      if (!event) return;
      const frameIndex = Number(frameId);
      const x = frameIndex % props.dates.length;
      const date = getDateTime(formatToSQL(props.dates[x]), 'utc').startOf(
        'day',
      );
      const frameEvents = events.filter((e) => e.x === x);
      const y = frameEvents.length;

      if (dragDirection === 'left' || dragDirection === 'right') {
        // to right
        if (x > draggingX) {
          if (x >= event.x + event.xSpan!) {
            setDraggingEvent({
              ...draggingEvent,
              xSpan: x - draggingEvent.x + 1,
              endedAt: date.plus({ day: 1 }),
            });
          } else {
            setDraggingEvent({
              ...draggingEvent,
              x,
              xSpan: event.x + event.xSpan! - x,
              startedAt: date,
            });
          }
        }
        // to left
        else if (x < draggingX) {
          if (x < event.x) {
            setDraggingEvent({
              ...draggingEvent,
              x,
              xSpan: event.x - x + 1,
              startedAt: date,
            });
          } else {
            setDraggingEvent({
              ...draggingEvent,
              x: event.x,
              xSpan: x - event.x + 1,
              endedAt: date.plus({ day: 1 }),
            });
          }
        }
        setDraggingX(x);
      } else {
        setDraggingEvent({
          ...event,
          x,
          y,
          startedAt: date,
          endedAt: date.plus({ day: event.xSpan }).startOf('day'),
        });
        return;
      }

      // if (dragDirection === 'left') {
      //   const xSpan = event.x - x;
      //   if (xSpan >= 0) {
      //     setDraggingEvent({
      //       ...draggingEvent,
      //       x,
      //       xSpan: xSpan + 1,
      //       startedAt: date,
      //     });
      //   }
      // }
      // else if (dragDirection === 'right') {
      //   const xSpan = x - event.x;
      //   if (xSpan >= 0) {
      //     setDraggingEvent({
      //       ...draggingEvent,
      //       xSpan: xSpan + 1,
      //       endedAt: event.startedAt.plus({ day: xSpan + 1 }),
      //     });
      //   }
      // }
    },
    [draggingEvent, events, dragDirection, props.dates],
  );

  const onEventDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const onEventDrop = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDropped(true);
    console.debug('drag drop');

    if (!draggingEvent) return;
    let currentIndex: number | null = null;
    for (let index = 0; index < schedules.length; index++) {
      const schedule = schedules[index];
      if (!schedule.isAllDay) continue;
      if (schedule.uid === draggingEvent.eventId) {
        currentIndex = index;
        break;
      }
    }
    if (currentIndex === null) {
      e.preventDefault();
      return;
    }
    const schedule = schedules[currentIndex];
    schedule.startedAt = getDateTime(
      formatToSQL(draggingEvent.startedAt),
      'utc',
    ).toISO();
    schedule.endedAt = getDateTime(
      formatToSQL(draggingEvent.endedAt),
      'utc',
    ).toISO();
    schedules[currentIndex] = schedule;

    dispatch(setSchedules(schedules));

    if (typeof props.onUpdateSchedule === 'function') {
      props.onUpdateSchedule(schedule);
    }
  };

  const onEventDragEnd = useCallback((e: DragEvent<HTMLDivElement>) => {
    console.debug('drag end');
  }, []);

  const onClickAddSlot = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      const { frameId } = e.currentTarget.dataset;

      dispatch(setSelectedSlots([]));
      dispatch(setSelectedGroups([]));
      dispatch(setCreatingEvent(null));

      const frameIndex = Number(frameId);
      const date = getDateTime(
        formatToSQL(props.dates[frameIndex]),
        state.timeZone,
      );
      const x = frameIndex % props.dates.length;
      const frameEvents = events.filter((e) => e.isAllDay && e.x === x);
      const y = frameEvents.length;

      const slot = {
        x,
        y,
        date,
        ySpan: 1,
        isAllDay: true,
      };
      dispatch(setSelectedSlots([slot]));
    },
    [state.selectedSlots, props.dates, state.rulerStep],
  );

  const onEventDragHandlerMove = useCallback(
    (e: DragEvent<HTMLDivElement>, direction: 'left' | 'right') => {
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
      const { eventId } = e.currentTarget.dataset;
      if (!eventId) return;
      const event = events.find((e) => e.eventId === eventId);
      if (!event) return;
      setDraggingEvent(event);
      setDraggingGhostEvent(event);
      setDragDirection(direction);
      disableEvents(event.eventId);
    },
    [events],
  );

  return {
    state: {
      draggingGhostEvent,
      draggingEvent,
      dragDirection,
      events,
      eventMaxRow: isCurrentPage ? eventMaxRow : 0,
    },
    actions: {
      onEventDragStart,
      onEventDragEnter,
      onEventDragOver,
      onEventDrop,
      onEventDragEnd,
      onClickAddSlot,
      onEventDragHandlerMove,
    },
  };
}
